import React from 'react';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Button,
  Chip,
  TextField,
} from '@mui/material';

export interface MultiSelectAutocompleteOption<T extends string | number> {
  value: T;
  label: string;
  searchText: string;
}

interface BaseMultiSelectAutocompleteProps<T extends string | number> {
  label: string;
  placeholder: string;
  options: MultiSelectAutocompleteOption<T>[];
  selectedValues: T[];
  setSelectedValues: (values: T[]) => void;
  disabled?: boolean;
  loading?: boolean;
}

interface MultiSelectAutocompleteWithActionsProps<T extends string | number>
  extends BaseMultiSelectAutocompleteProps<T> {
  selectAllLabel?: string;
  clearLabel?: string;
}

function BaseMultiSelectAutocomplete<T extends string | number>({
  label,
  placeholder,
  options,
  selectedValues,
  setSelectedValues,
  disabled = false,
  loading = false,
}: BaseMultiSelectAutocompleteProps<T>): React.ReactElement {
  const selectedOptions = selectedValues
    .map((value: T) =>
      options.find((option: MultiSelectAutocompleteOption<T>) => option.value === value)
    )
    .filter(
      (
        option: MultiSelectAutocompleteOption<T> | undefined
      ): option is MultiSelectAutocompleteOption<T> => !!option
    );

  return (
    <Autocomplete<MultiSelectAutocompleteOption<T>, true, false, false>
      multiple
      size="small"
      options={options}
      value={selectedOptions}
      disableCloseOnSelect
      loading={loading}
      disabled={disabled}
      limitTags={2}
      onChange={(
        _event: React.SyntheticEvent,
        newValue: MultiSelectAutocompleteOption<T>[]
      ): void => {
        setSelectedValues(newValue.map((option: MultiSelectAutocompleteOption<T>) => option.value));
      }}
      getOptionLabel={(option: MultiSelectAutocompleteOption<T>): string => option.label}
      isOptionEqualToValue={(
        option: MultiSelectAutocompleteOption<T>,
        value: MultiSelectAutocompleteOption<T>
      ): boolean => option.value === value.value}
      filterOptions={(
        currentOptions: MultiSelectAutocompleteOption<T>[],
        state: { inputValue: string }
      ): MultiSelectAutocompleteOption<T>[] => {
        const searchValue = state.inputValue.toLowerCase().trim();
        if (!searchValue) {
          return currentOptions;
        }
        return currentOptions.filter((option: MultiSelectAutocompleteOption<T>) =>
          option.searchText.includes(searchValue)
        );
      }}
      renderTags={(
        value: MultiSelectAutocompleteOption<T>[],
        getTagProps: AutocompleteRenderGetTagProps
      ): React.ReactElement[] =>
        value.map((option: MultiSelectAutocompleteOption<T>, index: number) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip label={option.label} size="small" key={key} {...tagProps} />;
        })
      }
      renderInput={(params: AutocompleteRenderInputParams): React.ReactElement => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={
            loading ? `Loading ${label.toLowerCase()}...` : `${selectedValues.length} selected`
          }
        />
      )}
    />
  );
}

function MultiSelectAutocompleteWithActions<T extends string | number>({
  label,
  placeholder,
  options,
  selectedValues,
  setSelectedValues,
  disabled = false,
  loading = false,
  selectAllLabel = 'Select all',
  clearLabel = 'Clear',
}: MultiSelectAutocompleteWithActionsProps<T>): React.ReactElement {
  return (
    <div className="border border-solid border-gray-300 rounded-md p-3">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="text-sm font-semibold">{label}</div>
        <div className="flex items-center gap-2">
          <Button
            size="small"
            disabled={disabled || loading || options.length === 0}
            onClick={(): void =>
              setSelectedValues(
                options.map((option: MultiSelectAutocompleteOption<T>) => option.value)
              )
            }
          >
            {selectAllLabel}
          </Button>
          <Button
            size="small"
            disabled={disabled || loading || selectedValues.length === 0}
            onClick={(): void => setSelectedValues([])}
          >
            {clearLabel}
          </Button>
        </div>
      </div>
      <BaseMultiSelectAutocomplete
        label={label}
        placeholder={placeholder}
        options={options}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        disabled={disabled}
        loading={loading}
      />
    </div>
  );
}

export { BaseMultiSelectAutocomplete, MultiSelectAutocompleteWithActions };

export default BaseMultiSelectAutocomplete;
